/**
 * Affiliate referral capture.
 *
 * A visitor arriving on any page with `?ref=CODE` gets the code remembered for
 * 30 days (first-touch: an existing pending code is not replaced). After they
 * sign in (email, Google or GitHub) AuthContext posts it to
 * /affiliate/attribute; the server only links accounts created in the last 24 h,
 * so a long-standing user logging in through someone's link changes nothing.
 * The signup form's "Referral code" field writes through the same store.
 */
const KEY = 'hyvay:ref';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const CODE_RE = /^[A-Za-z0-9]{4,20}$/;

interface Stored { code: string; at: number }

function read(): Stored | null {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const v = JSON.parse(raw) as Stored;
        if (!v?.code || !CODE_RE.test(v.code) || Date.now() - v.at > TTL_MS) {
            localStorage.removeItem(KEY);
            return null;
        }
        return v;
    } catch {
        return null;
    }
}

export function rememberReferral(code: string, overwrite = false): void {
    const c = code.trim().toUpperCase();
    if (!CODE_RE.test(c)) return;
    if (!overwrite && read()) return;
    try { localStorage.setItem(KEY, JSON.stringify({ code: c, at: Date.now() })); } catch { /* storage blocked */ }
}

/** Call once at boot: picks `ref` off the current URL and removes it from the address bar. */
export function captureReferralFromUrl(): void {
    try {
        const url = new URL(window.location.href);
        const ref = url.searchParams.get('ref');
        if (!ref) return;
        rememberReferral(ref);
        url.searchParams.delete('ref');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch { /* ignore */ }
}

export function pendingReferral(): string | null {
    return read()?.code ?? null;
}

export function clearReferral(): void {
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}
